import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import React from 'react';
import { bs } from '../../../../styles/styles';
import { InputFieldError } from '../../../InputFieldError';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../autocomplete/SimpleAutoCompleteSearchField';
import { isTagStep } from '../../../../../models/Automations';
import { styleSheet } from './../styles';

interface TagStepConfigProps {
	step: Api.IAutomationStep;
	stepType: Api.AutomationStepType.AddTag | Api.AutomationStepType.RemoveTag;
	canEdit: boolean;
	errorMessage: string | null;
	impersonationContext?: Api.IImpersonationContext;
	onClearErrorMessages: () => void;
	onSave: (newSelectedTag: string) => Promise<any>;
}

export function TagStepConfig(props: TagStepConfigProps) {
	const [selectedTag, setSelectedTag] = React.useState<string | null>(() => {
		const automationStep = props.step;
		if (!automationStep || !isTagStep(automationStep)) {
			return null;
		}

		// The step itself contains the tag data
		return (automationStep as Api.ITagAutomationStep).tagName || null;
	});

	const onTagSearchFieldChanged = (e: ISimpleAutoCompleteSearchFieldEvent<React.ChangeEvent<HTMLInputElement>>) => {
		if (e.sourceEvent) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			setSelectedTag(inputElement.value || '');
		}
	};

	const onTagSearchClearButtonClicked = () => {
		setSelectedTag(null);
		props.onSave(null);
	};

	const onSearchTagSelected = (
		e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>
	) => {
		if (!e.selection || typeof e.selection !== 'string') {
			return;
		}

		if (e.target) {
			e.target.setSearchQuery(e.selection);
		}
		const newSelectedTag = e.selection;
		setSelectedTag(newSelectedTag);
		props.onSave(newSelectedTag);
	};

	const onTagSearchFieldKeyDown = (e: ISimpleAutoCompleteSearchFieldEvent<React.KeyboardEvent<HTMLInputElement>>) => {
		if (!!e.sourceEvent && e.sourceEvent.keyCode === 13) {
			const inputElement = e.sourceEvent.target as HTMLInputElement;
			inputElement.blur();
		}
	};

	const baseKey = `${props.step?._type}-${props.step.id}-${props.step?.id || ''}`;

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				Select a tag: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={props.errorMessage}>
				<SimpleAutoCompleteSearchField
					disabled={!props.canEdit}
					impersonationContext={props.impersonationContext}
					initialSearchQuery={selectedTag || undefined}
					key={`${baseKey}-tag-select`}
					onBlur={() => props.onSave(selectedTag)}
					onChange={onTagSearchFieldChanged}
					onClear={onTagSearchClearButtonClicked}
					onFocus={props.onClearErrorMessages}
					onItemSelected={onSearchTagSelected}
					onKeyDown={onTagSearchFieldKeyDown}
					resultsLimit={10}
					pageSize={10}
					placeholder='Search or add a new tag'
					renderNoResultsItem={false}
					style={styleSheet.tagSearchField}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
				/>
			</InputFieldError>
		</>
	);
}
